'use client'
import type { FormFieldBlock, Form as FormType } from '@payloadcms/plugin-form-builder/types'

import { useRouter } from 'next/navigation'
import React, { useCallback, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import RichText from '@/components/RichText'
import { Button } from '@/components/ui/button'
import type { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'

import { fields } from './fields'
import { getClientSideURL } from '@/utilities/getURL'
import { prepareSubmissionData } from '@/utilities/formSubmission'
import { ChevronsRight } from 'lucide-react'
import { cn } from '@/utilities/ui'
import BlockWrapper from '@/components/BlockWrapper'
import TurnstileWidget from '@/components/TurnstileWidget'

export type FormBlockType = {
  blockName?: string
  blockType?: 'formBlock'
  enableIntro: boolean
  form: FormType
  introContent?: SerializedEditorState
  className?: string
}

export const FormBlock: React.FC<
  {
    id?: string
  } & FormBlockType
> = (props) => {
  const {
    enableIntro,
    form: formFromProps,
    form: { id: formID, confirmationMessage, confirmationType, redirect, submitButtonLabel } = {},
    introContent,
    className,
  } = props

  const formMethods = useForm({
    defaultValues: formFromProps.fields as any,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const [tsToken, setTsToken] = useState<string | null>(null)
  const [tsResetKey, setTsResetKey] = useState<number>(0)
  const [formTouched, setFormTouched] = useState(false)
  const router = useRouter()

  const onSubmit = useCallback(
    (data: FormFieldBlock[]) => {
      let loadingTimerID: ReturnType<typeof setTimeout>
      const submitForm = async () => {
        setError(undefined)

        const dataToSend = prepareSubmissionData(data as unknown as Record<string, unknown>)

        // delay loading indicator by 1s
        loadingTimerID = setTimeout(() => {
          setIsLoading(true)
        }, 1000)

        try {
          const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
              'cf-turnstile-response': tsToken,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          })

          const res = await req.json()

          clearTimeout(loadingTimerID)

          if (req.status >= 400) {
            setIsLoading(false)

            setError({
              message: res.errors?.[0]?.message || 'Internal Server Error',
              status: res.status,
            })
            // Force a new token for the next attempt
            setTsToken(null)
            setTsResetKey((v) => v + 1)

            return
          }

          setIsLoading(false)
          setHasSubmitted(true)
          // If remaining on the page, prep a fresh token just in case
          setTsToken(null)
          setTsResetKey((v) => v + 1)

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect

            const redirectUrl = url

            if (redirectUrl) router.push(redirectUrl)
          }
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({
            message: 'Something went wrong.',
          })
          // Force a fresh token after error
          setTsToken(null)
          setTsResetKey((v) => v + 1)
        }
      }

      void submitForm()
    },
    [router, formID, redirect, confirmationType, tsToken],
  )

  return (
    <BlockWrapper className="container lg:max-w-[48rem]">
      {enableIntro && introContent && !hasSubmitted && (
        <RichText
          className="mb-8 lg:mb-12"
          data={introContent}
          enableGutter={false}
          enableProse={false}
        />
      )}
      <div className={cn('p-4 lg:p-6 border border-border rounded-[0.8rem]', className)}>
        <FormProvider {...formMethods}>
          {!isLoading && hasSubmitted && confirmationType === 'message' && (
            <RichText data={confirmationMessage} />
          )}
          {isLoading && !hasSubmitted && <p>Loading, please wait...</p>}
          {error && <div>{`${error.status || '500'}: ${error.message || ''}`}</div>}
          {!hasSubmitted && (
            <form
              id={formID}
              onSubmit={handleSubmit(onSubmit)}
              onFocusCapture={() => {
                if (!formTouched) {
                  setFormTouched(true)
                }
              }}
            >
              <div className="mb-4 last:mb-0">
                {formFromProps &&
                  formFromProps.fields &&
                  formFromProps.fields?.map((field, index) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]
                    if (Field) {
                      return (
                        <div className="mb-6 last:mb-0" key={index}>
                          <Field
                            form={formFromProps}
                            {...field}
                            {...formMethods}
                            control={control}
                            errors={errors}
                            register={register}
                          />
                        </div>
                      )
                    }
                    return null
                  })}
              </div>

              {/* Turnstile widget */}
              <div className="mb-6">
                <TurnstileWidget
                  action="form_submit"
                  lazyLoad={true}
                  shouldLoad={formTouched}
                  onToken={(t) => setTsToken(t)}
                  onError={() => setTsToken(null)}
                  onExpire={() => setTsToken(null)}
                  resetSignal={tsResetKey}
                />
              </div>

              {/* <Button form={formID} type="submit" variant="default" >
                {submitButtonLabel}
              </Button> */}
              <div className="inline-flex justify-end relative group/link rounded-full overflow-hidden w-fit">
                <Button
                  form={formID}
                  type="submit"
                  disabled={!tsToken}
                  className="text-white bg-destructive hover:text-white hover:bg-black border border-destructive hover:border-none rounded-full w-fit group px-6 py-7 transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center gap-3 z-10">
                    {submitButtonLabel}
                    <ChevronsRight className="text-white" />
                  </span>
                </Button>
                <div
                  className={cn(
                    'z-0 absolute inset-0 bg-black origin-center rotate-[-45deg] scale-y-0 group-hover/link:scale-y-[5] transition-transform duration-500 ease-in-out rounded-full',
                  )}
                  aria-hidden="true"
                />
              </div>
            </form>
          )}
        </FormProvider>
      </div>
    </BlockWrapper>
  )
}



